<template>
  <div class="landing-page" :class="{ 'en-mode': locale === 'en' }">
    <!-- 导航栏 -->
    <nav class="navbar" :class="{ scrolled: isScrolled }">
      <div class="nav-container">
        <div class="logo">
          <span class="logo-icon">✨</span>
          <span class="logo-text">虚幻与现实</span>
        </div>
        <ul class="nav-links">
          <li><a href="#home" @click.prevent="scrollTo('home')">{{ t('nav.home') }}</a></li>
          <li><a href="#features" @click.prevent="scrollTo('features')">{{ t('nav.features') }}</a></li>
          <li><a href="#world" @click.prevent="scrollTo('world')">{{ t('nav.world') }}</a></li>
          <li><a href="#download" @click.prevent="scrollTo('download')">{{ t('nav.download') }}</a></li>
        </ul>
        <div class="lang-switch">
          <button :class="{ active: locale === 'zh' }" @click="setLang('zh')">中</button>
          <span class="divider">/</span>
          <button :class="{ active: locale === 'en' }" @click="setLang('en')">EN</button>
        </div>
        <button class="menu-toggle" @click="mobileMenuOpen = !mobileMenuOpen">
          <span></span><span></span><span></span>
        </button>
      </div>
      <div v-if="mobileMenuOpen" class="mobile-menu">
        <a href="#home" @click.prevent="scrollTo('home')">{{ t('nav.home') }}</a>
        <a href="#features" @click.prevent="scrollTo('features')">{{ t('nav.features') }}</a>
        <a href="#world" @click.prevent="scrollTo('world')">{{ t('nav.world') }}</a>
        <a href="#download" @click.prevent="scrollTo('download')">{{ t('nav.download') }}</a>
        <div class="mobile-lang">
          <button :class="{ active: locale === 'zh' }" @click="setLang('zh')">中文</button>
          <button :class="{ active: locale === 'en' }" @click="setLang('en')">English</button>
        </div>
      </div>
    </nav>

    <!-- Hero 区域 -->
    <section id="home" class="hero">
      <div class="hero-bg">
        <div class="gradient-orb orb-1"></div>
        <div class="gradient-orb orb-2"></div>
        <div class="stars"></div>
      </div>
      <div class="hero-container">
        <div class="hero-content">
          <div class="hero-badge">
            <span class="badge-text">{{ t('hero.official') }}</span>
            <span class="badge-arrow">↗</span>
          </div>
          <h1 class="hero-title">{{ t('hero.title') }}</h1>
          <p class="hero-subtitle">{{ t('hero.subtitle') }}</p>
          <p class="hero-desc">{{ t('hero.desc') }}</p>
          <div class="hero-tags">
            <span class="tag">{{ t('hero.tag1') }}</span>
            <span class="tag">{{ t('hero.tag2') }}</span>
            <span class="tag">{{ t('hero.tag3') }}</span>
            <span class="tag">{{ t('hero.tag4') }}</span>
          </div>
          <div class="hero-actions">
            <button class="btn-primary" @click="scrollTo('download')">
              <span>{{ t('nav.download') }}</span>
              <span class="btn-icon">↓</span>
            </button>
            <button class="btn-secondary" @click="scrollTo('features')">
              {{ t('nav.features') }}
            </button>
          </div>
        </div>
        <div class="hero-visual">
          <div class="floating-card card-main">
            <div class="world-illustration">
              <div class="floating-island island-1">
                <span class="castle">🏰</span>
                <div class="island-base"></div>
              </div>
              <div class="floating-island island-2">
                <span class="tree">🌲</span>
                <div class="island-base"></div>
              </div>
              <div class="floating-island island-3">
                <span class="mountain">⛰️</span>
                <div class="island-base"></div>
              </div>
              <div class="clouds">
                <span class="cloud">☁️</span>
                <span class="cloud">☁️</span>
                <span class="cloud">☁️</span>
              </div>
              <div class="sun">☀️</div>
            </div>
          </div>
          <div class="floating-card card-small card-npc">
            <span class="card-icon">🤖</span>
            <span class="card-label">AI NPC</span>
          </div>
          <div class="floating-card card-small card-memory">
            <span class="card-icon">🧠</span>
            <span class="card-label">Memory</span>
          </div>
          <div class="floating-card card-small card-co-op">
            <span class="card-icon">🎮</span>
            <span class="card-label">Co-op</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 核心特色 -->
    <section id="features" class="features">
      <div class="section-container">
        <div class="section-header">
          <h2>{{ t('features.title') }}</h2>
          <p>{{ t('features.subtitle') }}</p>
          <div class="header-line"></div>
        </div>
        <div class="feature-grid">
          <div class="feature-card">
            <div class="feature-icon" style="--icon-color: #ff6b9d;">🧠</div>
            <h3>{{ t('features.ai.title') }}</h3>
            <p>{{ t('features.ai.desc') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon" style="--icon-color: #4facfe;">💾</div>
            <h3>{{ t('features.memory.title') }}</h3>
            <p>{{ t('features.memory.desc') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon" style="--icon-color: #00f2fe;">🌍</div>
            <h3>{{ t('features.world.title') }}</h3>
            <p>{{ t('features.world.desc') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon" style="--icon-color: #a18cd1;">👥</div>
            <h3>{{ t('features.multiplayer.title') }}</h3>
            <p>{{ t('features.multiplayer.desc') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon" style="--icon-color: #fad0c4;">🎭</div>
            <h3>{{ t('features.roleplay.title') }}</h3>
            <p>{{ t('features.roleplay.desc') }}</p>
          </div>
          <div class="feature-card">
            <div class="feature-icon" style="--icon-color: #ff9a9e;">⚡</div>
            <h3>{{ t('features.weather.title') }}</h3>
            <p>{{ t('features.weather.desc') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 虚拟人生二次新生 -->
    <section id="world" class="virtual-life">
      <div class="section-container split">
        <div class="split-content">
          <h2 class="split-title">{{ t('virtualLife.title') }}</h2>
          <p class="split-desc">{{ t('virtualLife.desc') }}</p>
          <div class="split-list">
            <div class="split-item">
              <span class="item-dot" style="--dot-color: #ff6b35;"></span>
              <div class="item-text">
                <h4>{{ t('virtualLife.memoryModel.title') }}</h4>
                <p>{{ t('virtualLife.memoryModel.desc') }}</p>
              </div>
            </div>
            <div class="split-item">
              <span class="item-dot" style="--dot-color: #4facfe;"></span>
              <div class="item-text">
                <h4>{{ t('virtualLife.evolution.title') }}</h4>
                <p>{{ t('virtualLife.evolution.desc') }}</p>
              </div>
            </div>
            <div class="split-item">
              <span class="item-dot" style="--dot-color: #f9d423;"></span>
              <div class="item-text">
                <h4>{{ t('virtualLife.economy.title') }}</h4>
                <p>{{ t('virtualLife.economy.desc') }}</p>
              </div>
            </div>
            <div class="split-item">
              <span class="item-dot" style="--dot-color: #a18cd1;"></span>
              <div class="item-text">
                <h4>{{ t('virtualLife.social.title') }}</h4>
                <p>{{ t('virtualLife.social.desc') }}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="split-visual">
          <div class="globe-card">
            <div class="globe">🌍</div>
            <div class="orbit orbit-1"></div>
            <div class="orbit orbit-2"></div>
            <div class="particle p-1">✨</div>
            <div class="particle p-2">🌟</div>
            <div class="particle p-3">💫</div>
          </div>
        </div>
      </div>
    </section>

    <!-- 下载区域 -->
    <section id="download" class="download">
      <div class="download-bg">
        <div class="gradient-orb orb-3"></div>
      </div>
      <div class="section-container">
        <div class="download-content">
          <h2>{{ t('download.title') }}</h2>
          <p>{{ t('download.subtitle') }}</p>
          <div class="download-platforms">
            <button class="platform-btn" @click="download('windows')">
              <span class="platform-icon">🖥️</span>
              <span class="platform-text">{{ t('download.windows') }}</span>
            </button>
            <button class="platform-btn" @click="download('mac')">
              <span class="platform-icon">🍎</span>
              <span class="platform-text">{{ t('download.mac') }}</span>
            </button>
            <button class="platform-btn mobile" @click="showQr = !showQr">
              <span class="platform-icon">📱</span>
              <span class="platform-text">{{ t('download.mobile') }}</span>
            </button>
          </div>
          <div v-if="showQr" class="qr-area">
            <div class="qr-placeholder">
              <div class="qr-code">
                <div class="qr-pattern"></div>
              </div>
              <p>{{ t('download.qrTip') }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 页脚 -->
    <footer class="footer">
      <div class="footer-content">
        <div class="footer-logo">
          <span class="logo-icon">✨</span>
          <span>虚幻与现实</span>
        </div>
        <p class="footer-slogan">{{ t('footer.slogan') }}</p>
        <p class="footer-copy">{{ t('footer.copyright') }}</p>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()

const isScrolled = ref(false)
const mobileMenuOpen = ref(false)
const showQr = ref(false)

const handleScroll = () => {
  isScrolled.value = window.scrollY > 60
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

const setLang = (lang) => {
  locale.value = lang
  mobileMenuOpen.value = false
}

const scrollTo = (id) => {
  const el = document.getElementById(id)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' })
  }
  mobileMenuOpen.value = false
}

const download = (platform) => {
  // 占位逻辑：可替换为真实下载链接
  alert(`${platform} 下载链接即将开放`)
}
</script>

<style scoped>
/* ===== 基础变量 ===== */
.landing-page {
  --primary: #ff6b35;
  --primary-dark: #e85a2b;
  --dark-bg: #0f172a;
  --section-bg: #fff9f5;
  --text-dark: #1e293b;
  --text-muted: #64748b;
  --card-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  --radius: 24px;
  --max-width: 1200px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  color: var(--text-dark);
  overflow-x: hidden;
}

.landing-page.en-mode .hero-title {
  font-size: clamp(2.5rem, 6vw, 4.5rem);
}

/* ===== 导航栏 ===== */
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 16px 24px;
  transition: all 0.3s ease;
}

.navbar.scrolled {
  background: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(20px);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.15);
  padding: 12px 24px;
}

.nav-container {
  max-width: var(--max-width);
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  font-weight: 700;
  font-size: 1.25rem;
}

.logo-icon {
  font-size: 1.5rem;
}

.nav-links {
  display: flex;
  gap: 32px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.nav-links a {
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  transition: color 0.2s;
}

.nav-links a:hover {
  color: #fff;
}

.lang-switch {
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
}

.lang-switch button {
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px 6px;
  font-weight: 500;
  transition: color 0.2s;
}

.lang-switch button.active,
.lang-switch button:hover {
  color: #fff;
}

.menu-toggle {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 5px;
}

.menu-toggle span {
  width: 24px;
  height: 2px;
  background: #fff;
  border-radius: 2px;
}

.mobile-menu {
  display: none;
  flex-direction: column;
  gap: 16px;
  padding: 20px 24px;
  background: rgba(15, 23, 42, 0.98);
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
}

.mobile-menu a {
  color: #fff;
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
}

.mobile-lang {
  display: flex;
  gap: 16px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.mobile-lang button {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.7);
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
}

.mobile-lang button.active {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

/* ===== Hero 区域 ===== */
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  color: #fff;
  padding: 120px 24px 80px;
  overflow: hidden;
}

.hero-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.4;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: #4facfe;
  top: -100px;
  right: -100px;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: #ff6b35;
  bottom: -100px;
  left: -100px;
  opacity: 0.25;
}

.stars {
  position: absolute;
  inset: 0;
  background-image:
    radial-gradient(2px 2px at 20px 30px, rgba(255,255,255,0.6), transparent),
    radial-gradient(2px 2px at 40px 70px, rgba(255,255,255,0.4), transparent),
    radial-gradient(1px 1px at 90px 40px, rgba(255,255,255,0.5), transparent),
    radial-gradient(2px 2px at 160px 120px, rgba(255,255,255,0.3), transparent),
    radial-gradient(1px 1px at 230px 180px, rgba(255,255,255,0.4), transparent),
    radial-gradient(2px 2px at 300px 80px, rgba(255,255,255,0.5), transparent);
  background-size: 350px 220px;
}

.hero-container {
  max-width: var(--max-width);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  width: 100%;
  position: relative;
  z-index: 1;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--primary);
  color: #fff;
  padding: 8px 16px;
  border-radius: 100px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 24px;
  box-shadow: 0 8px 24px rgba(255, 107, 53, 0.3);
}

.hero-title {
  font-size: clamp(3rem, 7vw, 5.5rem);
  font-weight: 800;
  line-height: 1.1;
  margin: 0 0 16px;
  letter-spacing: -0.02em;
}

.hero-subtitle {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 20px;
}

.hero-desc {
  font-size: 1.15rem;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.85);
  max-width: 560px;
  margin: 0 0 28px;
}

.hero-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 32px;
}

.tag {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.9);
  padding: 8px 16px;
  border-radius: 100px;
  font-size: 0.9rem;
  backdrop-filter: blur(10px);
  transition: all 0.2s;
}

.tag:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
}

.hero-actions {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.btn-primary,
.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 32px;
  border-radius: 100px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  box-shadow: 0 10px 30px rgba(255, 107, 53, 0.35);
}

.btn-primary:hover {
  background: var(--primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 14px 40px rgba(255, 107, 53, 0.45);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
}

.btn-icon {
  font-size: 1.1rem;
}

/* Hero 视觉 */
.hero-visual {
  position: relative;
  height: 500px;
}

.floating-card {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius);
  backdrop-filter: blur(20px);
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.2);
}

.card-main {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 420px;
  height: 320px;
  overflow: hidden;
  animation: float 6s ease-in-out infinite;
}

.card-small {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 84px;
  height: 84px;
  animation: float 5s ease-in-out infinite;
}

.card-npc {
  top: 40px;
  right: 40px;
  animation-delay: -1s;
}

.card-memory {
  bottom: 80px;
  left: 20px;
  animation-delay: -2s;
}

.card-co-op {
  bottom: 40px;
  right: 80px;
  animation-delay: -3s;
}

.card-icon {
  font-size: 1.8rem;
}

.card-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
}

.world-illustration {
  width: 100%;
  height: 100%;
  position: relative;
  background: linear-gradient(180deg, #1e3a5f 0%, #0f172a 100%);
  border-radius: var(--radius);
  overflow: hidden;
}

.floating-island {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: float 4s ease-in-out infinite;
}

.island-1 {
  top: 50px;
  left: 60px;
  font-size: 3.5rem;
  animation-delay: -0.5s;
}

.island-2 {
  top: 120px;
  right: 70px;
  font-size: 2.5rem;
  animation-delay: -1.5s;
}

.island-3 {
  bottom: 50px;
  left: 120px;
  font-size: 3rem;
  animation-delay: -2.5s;
}

.island-base {
  width: 90px;
  height: 20px;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 50%;
  margin-top: -8px;
}

.clouds {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.cloud {
  position: absolute;
  opacity: 0.6;
  animation: drift 20s linear infinite;
}

.cloud:nth-child(1) { top: 30px; left: -20px; font-size: 2.5rem; }
.cloud:nth-child(2) { top: 150px; right: -10px; font-size: 2rem; animation-delay: -8s; }
.cloud:nth-child(3) { bottom: 80px; left: 30px; font-size: 1.8rem; animation-delay: -14s; }

.sun {
  position: absolute;
  top: 20px;
  right: 30px;
  font-size: 2.5rem;
  animation: pulse 4s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-16px); }
}

@keyframes drift {
  0% { transform: translateX(-30px); }
  50% { transform: translateX(30px); }
  100% { transform: translateX(-30px); }
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50% { transform: scale(1.1); opacity: 1; }
}

/* ===== 通用区块 ===== */
section {
  padding: 100px 24px;
}

.section-container {
  max-width: var(--max-width);
  margin: 0 auto;
}

.section-header {
  text-align: center;
  margin-bottom: 64px;
}

.section-header h2 {
  font-size: clamp(2rem, 4vw, 2.75rem);
  font-weight: 800;
  margin: 0 0 12px;
  color: var(--text-dark);
}

.section-header p {
  font-size: 1.1rem;
  color: var(--text-muted);
  margin: 0;
}

.header-line {
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, var(--primary), #ff9a7b);
  border-radius: 4px;
  margin: 20px auto 0;
}

/* ===== 核心特色 ===== */
.features {
  background: var(--section-bg);
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
}

.feature-card {
  background: #fff;
  border-radius: var(--radius);
  padding: 40px 32px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.03);
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: var(--card-shadow);
}

.feature-icon {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  margin: 0 auto 20px;
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  color: var(--icon-color);
}

.feature-card h3 {
  font-size: 1.2rem;
  font-weight: 700;
  margin: 0 0 12px;
}

.feature-card p {
  font-size: 0.95rem;
  line-height: 1.7;
  color: var(--text-muted);
  margin: 0;
}

/* ===== 虚拟人生 ===== */
.virtual-life {
  background: linear-gradient(180deg, #fff 0%, #fff9f5 100%);
}

.split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
}

.split-title {
  font-size: clamp(2.2rem, 4.5vw, 3.5rem);
  font-weight: 800;
  line-height: 1.2;
  margin: 0 0 20px;
  white-space: pre-line;
}

.split-desc {
  font-size: 1.1rem;
  color: var(--text-muted);
  line-height: 1.8;
  margin: 0 0 40px;
  max-width: 480px;
}

.split-list {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.split-item {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.item-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--dot-color);
  margin-top: 8px;
  flex-shrink: 0;
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.5), 0 0 0 6px rgba(255, 107, 53, 0.1);
}

.item-text h4 {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 6px;
  color: var(--dot-color);
}

.item-text p {
  font-size: 0.95rem;
  line-height: 1.7;
  color: var(--text-muted);
  margin: 0;
}

.split-visual {
  display: flex;
  justify-content: center;
  align-items: center;
}

.globe-card {
  position: relative;
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-radius: var(--radius);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 40px 80px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.globe {
  font-size: 10rem;
  z-index: 2;
  animation: float 5s ease-in-out infinite;
}

.orbit {
  position: absolute;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  animation: spin 10s linear infinite;
}

.orbit-1 {
  width: 300px;
  height: 300px;
}

.orbit-2 {
  width: 360px;
  height: 360px;
  animation-duration: 14s;
  animation-direction: reverse;
  border-color: rgba(255, 255, 255, 0.06);
}

.particle {
  position: absolute;
  font-size: 1.2rem;
  animation: twinkle 2s ease-in-out infinite;
}

.p-1 { top: 60px; left: 60px; }
.p-2 { top: 80px; right: 70px; animation-delay: -0.7s; }
.p-3 { bottom: 80px; left: 90px; animation-delay: -1.4s; }

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes twinkle {
  0%, 100% { opacity: 0.4; transform: scale(0.9); }
  50% { opacity: 1; transform: scale(1.1); }
}

/* ===== 下载区域 ===== */
.download {
  position: relative;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: #fff;
  padding: 100px 24px;
  overflow: hidden;
}

.download-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.orb-3 {
  width: 400px;
  height: 400px;
  background: #4facfe;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.15;
}

.download-content {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 700px;
  margin: 0 auto;
}

.download-content h2 {
  font-size: clamp(2rem, 4vw, 2.75rem);
  font-weight: 800;
  margin: 0 0 16px;
}

.download-content p {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 40px;
  line-height: 1.7;
}

.download-platforms {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.platform-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #fff;
  padding: 16px 28px;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(10px);
  min-width: 180px;
  justify-content: center;
}

.platform-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.platform-btn.mobile {
  background: linear-gradient(135deg, var(--primary), var(--primary-dark));
  border-color: transparent;
}

.platform-btn.mobile:hover {
  box-shadow: 0 10px 30px rgba(255, 107, 53, 0.35);
}

.platform-icon {
  font-size: 1.5rem;
}

.platform-text {
  font-weight: 600;
  font-size: 1rem;
}

.qr-area {
  margin-top: 40px;
  animation: fadeIn 0.3s ease;
}

.qr-placeholder {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 24px;
  border-radius: 20px;
}

.qr-code {
  width: 140px;
  height: 140px;
  background: #fff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
}

.qr-pattern {
  width: 100%;
  height: 100%;
  background: repeating-linear-gradient(
    0deg,
    #1e293b 0px,
    #1e293b 6px,
    transparent 6px,
    transparent 12px
  ),
  repeating-linear-gradient(
    90deg,
    #1e293b 0px,
    #1e293b 6px,
    transparent 6px,
    transparent 12px
  );
  border-radius: 4px;
}

.qr-placeholder p {
  margin: 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ===== 页脚 ===== */
.footer {
  background: #0b1120;
  color: rgba(255, 255, 255, 0.6);
  padding: 60px 24px;
  text-align: center;
}

.footer-logo {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 16px;
}

.footer-slogan {
  font-size: 1rem;
  margin: 0 0 12px;
}

.footer-copy {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
}

/* ===== 响应式 ===== */
@media (max-width: 992px) {
  .hero-container,
  .split {
    grid-template-columns: 1fr;
    gap: 50px;
  }

  .hero-visual {
    order: -1;
    height: 380px;
  }

  .card-main {
    width: 320px;
    height: 240px;
  }

  .card-npc { right: 10px; top: 20px; }
  .card-memory { left: 10px; bottom: 60px; }
  .card-co-op { right: 30px; bottom: 20px; }

  .feature-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .globe-card {
    width: 320px;
    height: 320px;
  }
}

@media (max-width: 768px) {
  .nav-links,
  .lang-switch {
    display: none;
  }

  .menu-toggle,
  .mobile-menu {
    display: flex;
  }

  .hero {
    padding: 100px 20px 60px;
  }

  .hero-desc {
    font-size: 1rem;
  }

  .hero-tags {
    gap: 8px;
  }

  .tag {
    font-size: 0.8rem;
    padding: 6px 12px;
  }

  .btn-primary,
  .btn-secondary {
    padding: 12px 24px;
  }

  section {
    padding: 70px 20px;
  }

  .feature-grid {
    grid-template-columns: 1fr;
  }

  .split {
    gap: 50px;
  }

  .split-title {
    white-space: normal;
  }

  .download-platforms {
    flex-direction: column;
    align-items: stretch;
  }

  .platform-btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .hero-visual {
    height: 300px;
  }

  .card-main {
    width: 260px;
    height: 200px;
  }

  .card-small {
    width: 64px;
    height: 64px;
  }

  .card-icon {
    font-size: 1.4rem;
  }

  .card-label {
    font-size: 0.65rem;
  }

  .globe-card {
    width: 260px;
    height: 260px;
  }

  .globe {
    font-size: 6rem;
  }
}
</style>
